import {
    Alert,
    Autocomplete,
    Button,
    Chip,
    Link,
    Paper,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    createFilterOptions,
} from '@mui/material'
import {
    DataGridPro,
    GridColDef,
    GridRenderCellParams,
} from '@mui/x-data-grid-pro'
import { LoadingButton } from '@mui/lab'
import { SnackbarProvider } from 'notistack'
import { useMemo } from 'react'
import DashboardLayout from '@pages/Dashboard/components/DashboardLayout'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SyncIcon from '@mui/icons-material/Sync'
import ValidationErrors from '@components/ValidationErrors'
import { XeroContactOption, XeroRegion } from '@types'
import useOrgXeroContactsPage, {
    OrgXeroContactGridRow,
} from './useOrgXeroContactsPage'

/**
 * Both tenants together run to thousands of contacts, so the dropdown only
 * renders what a search narrows it down to.
 */
const filterContacts = createFilterOptions<XeroContactOption>({
    limit: 50,
    stringify: (contact) => contact.name,
})

const ContactLink = (props: { name: string; url: string | null }) => {
    if (!props.name) {
        return <Typography variant="body2">—</Typography>
    }

    if (!props.url) {
        return <Typography variant="body2">{props.name}</Typography>
    }

    return (
        <Link
            href={props.url}
            target="_blank"
            rel="noreferrer"
            underline="hover"
            variant="body2"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
            {props.name}
            <OpenInNewIcon sx={{ fontSize: 14 }} />
        </Link>
    )
}

const columns = ({
    contactsByRegion,
    contactById,
    pending,
    setAssignment,
}: {
    contactsByRegion: Record<XeroRegion, XeroContactOption[]>
    contactById: Map<string, XeroContactOption>
    pending: Record<string, string>
    setAssignment: (
        row: OrgXeroContactGridRow,
        contactId: string | null
    ) => void
}): GridColDef<OrgXeroContactGridRow>[] => [
    {
        field: 'orgName',
        headerName: 'Organisation',
        flex: 1,
        minWidth: 200,
    },
    {
        field: 'region',
        headerName: 'Region',
        width: 90,
        valueGetter: (_value, row) => row.region ?? 'n/a',
    },
    {
        field: 'status',
        headerName: 'Status',
        width: 130,
        valueGetter: (_value, row) => {
            if (pending[row.orgId]) return 'Pending'
            if (!row.region) return 'Not invoiceable'
            return row.currentXeroContactId ? 'Linked' : 'Unlinked'
        },
        renderCell: (params: GridRenderCellParams) => {
            const status = params.value as string

            return (
                <Chip
                    size="small"
                    label={status}
                    color={
                        status === 'Pending'
                            ? 'warning'
                            : status === 'Linked'
                              ? 'success'
                              : 'default'
                    }
                    variant={status === 'Linked' ? 'filled' : 'outlined'}
                />
            )
        },
    },
    {
        field: 'currentXeroContactName',
        headerName: 'Current contact',
        flex: 1,
        minWidth: 180,
        display: 'flex',
        renderCell: (params: GridRenderCellParams<OrgXeroContactGridRow>) => (
            <ContactLink
                name={params.row.currentXeroContactName}
                url={params.row.currentXeroContactUrl}
            />
        ),
    },
    {
        field: 'suggestedXeroContactName',
        headerName: 'Suggested (name match)',
        flex: 1,
        minWidth: 200,
        display: 'flex',
        renderCell: (params: GridRenderCellParams<OrgXeroContactGridRow>) => {
            const { row } = params

            const alreadySet =
                !row.suggestedXeroContactId ||
                row.suggestedXeroContactId === row.currentXeroContactId ||
                row.suggestedXeroContactId === pending[row.orgId]

            return (
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    sx={{ width: '100%' }}
                >
                    <ContactLink
                        name={row.suggestedXeroContactName}
                        url={row.suggestedXeroContactUrl}
                    />
                    {!alreadySet && (
                        <Button
                            size="small"
                            onClick={() =>
                                setAssignment(row, row.suggestedXeroContactId)
                            }
                        >
                            Use
                        </Button>
                    )}
                </Stack>
            )
        },
    },
    {
        field: 'assign',
        headerName: 'Assign contact',
        width: 320,
        sortable: false,
        filterable: false,
        display: 'flex',
        renderCell: (params: GridRenderCellParams<OrgXeroContactGridRow>) => {
            const { row } = params

            /**
             * Orgs outside the US and Canadian tenants (e.g. UK) aren't invoiced
             * through these Xero orgs, so there's nothing to link them to.
             */
            if (!row.region) {
                return (
                    <Typography variant="body2" color="text.secondary">
                        —
                    </Typography>
                )
            }

            const selectedId = pending[row.orgId] ?? row.currentXeroContactId

            return (
                <Autocomplete
                    size="small"
                    fullWidth
                    options={contactsByRegion[row.region]}
                    value={(selectedId && contactById.get(selectedId)) || null}
                    filterOptions={filterContacts}
                    getOptionLabel={(contact) => contact.name}
                    isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                    }
                    onChange={(_event, contact) =>
                        setAssignment(row, contact?.id ?? null)
                    }
                    /**
                     * The grid reads a space as page down and moves focus off the
                     * row, so it never reaches the search box. Org names have
                     * spaces in them, so keep it here.
                     */
                    onKeyDown={(event) => {
                        if (event.key === ' ') event.stopPropagation()
                    }}
                    renderInput={(inputParams) => (
                        <TextField
                            {...inputParams}
                            variant="standard"
                            placeholder="Search contacts…"
                        />
                    )}
                />
            )
        },
    },
]

export default function OrgXeroContactsPage() {
    const {
        loading,
        saving,
        loaded,
        error,
        rows,
        visibleRows,
        contactsByRegion,
        contactById,
        pending,
        pendingCount,
        saveResult,
        view,
        setView,
        searchText,
        setSearchText,
        loadOptions,
        setAssignment,
        acceptSuggestions,
        clearPending,
        save,
    } = useOrgXeroContactsPage()

    const gridColumns = useMemo(
        () =>
            columns({
                contactsByRegion,
                contactById,
                pending,
                setAssignment,
            }),
        [contactsByRegion, contactById, pending, setAssignment]
    )

    /**
     * Nothing is fetched on mount - loading the contacts costs us Xero API calls,
     * so the page stays inert until it's asked for.
     */
    if (!loaded) {
        return (
            <DashboardLayout loading={loading}>
                <Stack
                    alignItems="center"
                    sx={{ paddingTop: 8, textAlign: 'center' }}
                >
                    <Paper sx={{ padding: 4, maxWidth: '38em' }}>
                        <Typography variant="h5" component="h2" gutterBottom>
                            Assign Orgs to Xero Contacts
                        </Typography>
                        <Typography sx={{ marginBottom: 2 }}>
                            This page links each organisation to the Xero
                            contact it should be invoiced against.
                        </Typography>
                        <Typography color="text.secondary">
                            The contact list comes from Xero, and we're careful
                            with how often we ask them for it, so nothing is
                            loaded until you click below. Until then the table
                            and dropdowns aren't available.
                        </Typography>
                        <LoadingButton
                            loading={loading}
                            loadingPosition="start"
                            startIcon={<SyncIcon />}
                            variant="contained"
                            onClick={loadOptions}
                            sx={{ marginTop: 4 }}
                        >
                            Load Xero Contacts
                        </LoadingButton>
                        {loading && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ marginTop: 2 }}
                            >
                                Paging through both Xero tenants, this can take
                                a little while…
                            </Typography>
                        )}
                        {error && (
                            <Alert severity="error" sx={{ marginTop: 4 }}>
                                {error}
                            </Alert>
                        )}
                    </Paper>
                </Stack>
            </DashboardLayout>
        )
    }

    return (
        <DashboardLayout loading={loading || saving}>
            {error && (
                <Alert severity="error" sx={{ marginBottom: 2 }}>
                    {error}
                </Alert>
            )}

            <Stack
                direction="row"
                alignItems="flex-end"
                justifyContent="space-between"
                gap={2}
                sx={{ marginBottom: 2 }}
            >
                <TextField
                    variant="standard"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    placeholder="Search…"
                />
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={view}
                    onChange={(_event, value) => value && setView(value)}
                >
                    <ToggleButton value="unlinked">Unlinked</ToggleButton>
                    <ToggleButton value="suggested">Suggested</ToggleButton>
                    <ToggleButton value="all">All</ToggleButton>
                </ToggleButtonGroup>
                <Typography
                    variant="body2"
                    sx={{ color: 'var(--grey-700)', marginBottom: 0.5 }}
                >
                    <strong>
                        {visibleRows.length} of {rows.length} organisations
                    </strong>
                </Typography>
                <Stack direction="row" alignItems="center" gap={1}>
                    <LoadingButton
                        size="small"
                        loading={loading}
                        loadingPosition="start"
                        startIcon={<SyncIcon />}
                        disabled={loading || saving}
                        onClick={() => {
                            // Another Xero fetch, so make it a deliberate choice
                            if (
                                pendingCount > 0 &&
                                !confirm(
                                    'Reloading from Xero will discard your unsaved changes. Continue?'
                                )
                            ) {
                                return
                            }
                            loadOptions()
                        }}
                    >
                        Reload
                    </LoadingButton>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => acceptSuggestions(visibleRows)}
                    >
                        Use All Suggestions
                    </Button>
                    <Button
                        size="small"
                        disabled={pendingCount === 0}
                        onClick={clearPending}
                    >
                        Discard
                    </Button>
                    <LoadingButton
                        loading={saving}
                        disabled={pendingCount === 0}
                        variant="contained"
                        size="small"
                        onClick={save}
                    >
                        Save {pendingCount > 0 && `(${pendingCount})`}
                    </LoadingButton>
                </Stack>
            </Stack>

            <DataGridPro
                rows={visibleRows}
                columns={gridColumns}
                getRowId={(row) => row.orgId}
                rowHeight={58}
                pagination
                pageSizeOptions={[10, 20, 50, { value: -1, label: 'All' }]}
                initialState={{
                    pagination: { paginationModel: { pageSize: 20 } },
                }}
                disableRowSelectionOnClick
            />

            {saveResult && saveResult.failed.length > 0 && (
                <ValidationErrors
                    messages={saveResult.failed.map(
                        (failure) => `${failure.orgId}: ${failure.reason}`
                    )}
                />
            )}

            <SnackbarProvider
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />
        </DashboardLayout>
    )
}
